import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
	fetchCurrentUser,
	fetchUserBankAccount,
	updateUser,
	updateUserProfileImage,
	fetchCaddieList,
	fetchCaddieById,
	fetchProById,
	getAllSponsors,
	getAllProAgencies,
	updateTourProInfo,
	updateTourCaddieInfo,
	fetchProList,
	fetchProListByTournaments,
	fetchPartnerUnavailable,
	createUserBankAccount,
	updateUserBankAccount,
} from '../services/api'
import { useToken } from '../utils/auth'

/**
 * 현재 로그인한 사용자 정보를 조회하는 훅
 * React Query로 캐싱 및 자동 관리
 */
export const useUser = () => {
	const token = useToken()

	return useQuery({
		queryKey: ['user', 'me'],
		queryFn: () => fetchCurrentUser(token),
		enabled: !!token, // 토큰이 있을 때만 실행
		staleTime: 1000 * 60 * 5, // 5분간 캐시 유지
		retry: 1,
		select: (data) => {
			if (data == null) return data
			return {
				...data,
				userType: data.user_type, // 하위 호환성을 위한 필드 추가
			}
		},
	})
}

/**
 * 사용자 정보를 업데이트하는 mutation 훅
 */
export const useUpdateUser = () => {
	const token = useToken()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data) => updateUser(token, data),
		onSuccess: () => {
			// 사용자 정보 캐시 무효화 및 리페칭
			queryClient.invalidateQueries(['user', 'me'])
		},
		onError: (error) => {
			console.error('사용자 정보 업데이트 실패:', error)
		},
	})
}

/**
 * 프로필 이미지를 업데이트하는 mutation 훅
 */
export const useUpdateProfileImage = () => {
	const token = useToken()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, imageUrl }) => updateUserProfileImage(token, userId, imageUrl),
		onSuccess: () => {
			// 사용자 정보 캐시 무효화 및 리페칭
			queryClient.invalidateQueries(['user', 'me'])
		},
		onError: (error) => {
			console.error('프로필 이미지 업데이트 실패:', error)
		},
	})
}

/**
 * 캐디 목록을 검색하는 훅
 * @param {object} params - 검색 파라미터
 * @param {boolean} params.onlyTour - 투어캐디만 조회
 * @param {string[]} params.tournamentIds - 해당 대회 기간과 겹치는 완전 체결(ACCEPTED) 계약이 있는 캐디 제외
 * @param {number} params.periodYear - 기간계약 연도
 * @param {string[]} params.periodHalf - 기간계약 상반기/하반기 ('first_half', 'second_half')
 * @param {boolean} enabled - 쿼리 활성화 여부 (기본: true)
 */
export const useSearchCaddies = (
	{ onlyTour = false, tournamentIds, periodYear, periodHalf } = {},
	enabled = true,
) => {
	const token = useToken()
	return useQuery({
		queryKey: [
			'caddies',
			onlyTour,
			(tournamentIds || []).join(','),
			periodYear,
			(periodHalf || []).join(','),
		],
		queryFn: () => fetchCaddieList({ onlyTour, tournamentIds, periodYear, periodHalf }, token),
		enabled,
		staleTime: 1000 * 60 * 5, // 5분간 캐시 유지
	})
}

/**
 * 상대방(캐디/프로)이 이미 계약 중인 대회·기간 정보 (계약 조건 비활성화용)
 * @param {string} partnerUserId - 상대방 user id
 * @param {number} [year] - 기준 연도
 * @param {boolean} enabled - 쿼리 활성화 여부
 */
export const usePartnerUnavailable = (partnerUserId, year, enabled = true) => {
	const token = useToken()
	return useQuery({
		queryKey: ['partnerUnavailable', partnerUserId, year],
		queryFn: () => fetchPartnerUnavailable(token, partnerUserId, year),
		enabled: !!token && !!partnerUserId && enabled,
		staleTime: 1000 * 60 * 2, // 2분
	})
}

/**
 * 특정 캐디의 프로필 정보를 조회하는 훅
 * @param {string} caddieId - 캐디 ID
 * @param {boolean} enabled - 쿼리 활성화 여부 (기본: true)
 */
export const useCaddie = (caddieId, enabled = true) => {
	return useQuery({
		queryKey: ['caddie', caddieId],
		queryFn: () => fetchCaddieById(caddieId),
		enabled: !!caddieId && enabled,
		staleTime: 1000 * 60 * 5, // 5분간 캐시 유지
	})
}

/**
 * 특정 프로의 프로필 정보를 조회하는 훅
 * @param {string} proId - 프로 ID
 * @param {boolean} enabled - 쿼리 활성화 여부 (기본: true)
 */
export const usePro = (proId, enabled = true) => {
	const token = useToken()
	return useQuery({
		queryKey: ['pro', proId],
		queryFn: () => fetchProById(token, proId),
		enabled: !!proId && !!token && enabled,
		staleTime: 1000 * 60 * 5, // 5분간 캐시 유지
	})
}

/**
 * 스폰서 목록을 가져오는 훅 (모든 스폰서)
 */
export const useSponsors = () => {
	return useQuery({
		queryKey: ['sponsors'],
		queryFn: () => getAllSponsors(),
		select: (data) => {
			// API 응답을 select 옵션 형태로 변환
			const options = data.map((sponsor) => ({
				value: sponsor.id ? sponsor.id.toString() : sponsor.name || sponsor,
				label: sponsor.name || sponsor,
			}))
			return options
		},
	})
}

/**
 * 프로 에이젼시 목록을 가져오는 훅
 */
export const useProAgencies = () => {
	return useQuery({
		queryKey: ['proAgencies'],
		queryFn: getAllProAgencies,
		select: (data) => {
			// API 응답을 select 옵션 형태로 변환
			const options = data.map((agency) => ({
				value: agency.id ? agency.id.toString() : agency.name || agency,
				label: agency.name || agency,
			}))
			return options
		},
	})
}

/**
 * 투어 프로 정보 수정을 위한 mutation 훅
 */
export const useUpdateTourProInfo = () => {
	const token = useToken()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (updateData) => updateTourProInfo(token, updateData),
		onSuccess: () => {
			// useUser와 동일한 쿼리 키로 무효화 후 즉시 재요청
			queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
			queryClient.refetchQueries({ queryKey: ['user', 'me'] })
		},
	})
}

/**
 * 투어캐디 정보 수정을 위한 mutation 훅
 */
export const useUpdateTourCaddieInfo = () => {
	const token = useToken()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (updateData) => updateTourCaddieInfo(token, updateData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
			queryClient.refetchQueries({ queryKey: ['user', 'me'] })
		},
		onError: (error) => {
			console.error('캐디 정보 업데이트 실패:', error)
		},
	})
}

const BANK_ACCOUNT_QUERY_KEY = ['bankAccount']

/**
 * 사용자 계좌 정보 조회
 */
export const useBankAccount = () => {
	const token = useToken()

	return useQuery({
		queryKey: BANK_ACCOUNT_QUERY_KEY,
		queryFn: () => fetchUserBankAccount(token),
		enabled: !!token,
		retry: 1,
		staleTime: 1000 * 30,
	})
}

/**
 * 사용자 계좌 정보 생성
 */
export const useCreateBankAccount = () => {
	const token = useToken()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (payload) => createUserBankAccount(token, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: BANK_ACCOUNT_QUERY_KEY })
		},
	})
}

/**
 * 사용자 계좌 정보 수정
 */
export const useUpdateBankAccount = () => {
	const token = useToken()
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (payload) => updateUserBankAccount(token, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: BANK_ACCOUNT_QUERY_KEY })
		},
	})
}

/**
 * 프로 선수 목록을 조회하는 훅 (사전계약 필터링 옵션)
 * @param {boolean} withoutPreContract - 사전계약 제외 여부 (선택사항)
 * @param {boolean} enabled - 쿼리 활성화 여부 (기본: true)
 */
export const useProList = (params = '', enabled = true) => {
	const token = useToken()
	// Backward-compatible: allow string (keyword) or object { keyword, tournamentIds } — queryKey 구분용
	let keyword = ''
	let tournamentIds = []

	if (typeof params === 'string') {
		keyword = params
	} else if (params && typeof params === 'object') {
		keyword = params.keyword || ''
		tournamentIds = params.tournamentIds || []
	}

	return useQuery({
		queryKey: ['proList', keyword, (tournamentIds || []).join(',')],
		queryFn: () =>
			tournamentIds?.length > 0
				? fetchProListByTournaments(token, tournamentIds)
				: fetchProList(token),
		enabled: !!token && enabled,
		staleTime: 1000 * 60 * 5, // 5분간 캐시 유지
	})
}
